import { Article, Payment } from "../types/receipt";
import { formatMerchantLocalDate } from "./dateFormatting";

export const formatCurrency = (
  value: number,
  currencyCode: string,
  hideCurrency: boolean = false,
  convertToNegativeValue: boolean = false
): string => {
  let floatValue = parseFloat(value.toString());

  if (convertToNegativeValue) {
    floatValue = -floatValue;
  }

  if (hideCurrency) {
    const formatter = new Intl.NumberFormat(undefined, {
      style: "decimal",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    return formatter.format(floatValue);
  }

  const formatter = new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: currencyCode,
    currencyDisplay: "code",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  return formatter.format(floatValue);
};

export const formatPercentage = (
  decimalValue: number | undefined | null
): string => {
  if (decimalValue == null) return "0%";

  const percentage = decimalValue * 100;

  return percentage % 1 === 0 ? `${percentage.toFixed(0)}%` : `${percentage}%`;
};

export const formatDate = (
  dateString: string | undefined,
  countryCode: string,
  includeSeconds: boolean = true
) => {
  return formatMerchantLocalDate(dateString, countryCode, includeSeconds);
};

export const isNorwegian = (lang: string) => lang === "nor";

export const translate = (lang: string, norwegian: string, english: string) => {
  return isNorwegian(lang) ? norwegian : english;
};

export const formatAddress = (address: {
  address?: string;
  zip_code?: string;
  city?: string;
  country_code?: string;
}) => {
  const parts = [];

  if (address.address) parts.push(address.address);
  if (address.zip_code) parts.push(address.zip_code);
  if (address.city) parts.push(address.city);
  if (address.country_code) parts.push(address.country_code);

  return parts.join(", ");
};

export const formatPaymentMethod = (
  paymentMethod: string | undefined
): string => {
  if (!paymentMethod) {
    return "";
  }
  return paymentMethod.replace(/_/g, " ");
};

export const getPaymentMethodDisplay = (payment: Payment) => {
  const displays = [];
  if (payment.payment_method) displays.push(payment.payment_method);
  if (payment.payment_scheme) displays.push(payment.payment_scheme);
  if (payment.issuer_name) displays.push(payment.issuer_name);
  if (payment.issuer_number) displays.push(payment.issuer_number);
  return displays.join(" ");
};

export const getQuantityType = (
  quantityType: string | undefined | null,
  lang: string
): string => {
  if (quantityType) return ` ${quantityType}`;
  return translate(lang, " stk", " pcs");
};

export const getArticleDisplay = (article: Article, lang: string = "eng") => {
  const displays = [];
  if (article.art_name) displays.push(article.art_name);
  if (article.quantity) {
    displays.push(`${article.quantity}${getQuantityType(article.quantity_type, lang)}`);
  }
  return displays.join(" - ");
};

export const getArticleButtonText = (
  type: string,
  language: string
): string => {
  if (type === "ticket") {
    return translate(language, "Vis billett", "Show ticket");
  }
  if (type === "travel_ticket") {
    return translate(language, "Vis reisebillett", "Show travel ticket");
  }
  if (type === "credit") {
    return translate(language, "Vis gavekort", "Show Gift card");
  }
  return translate(language, "Vis gavekort", `Show ${type}`);
};

export const getArticleTypeDisplay = (
  type: string | undefined,
  lang: string
) => {
  if (!type) return "";

  if (type === "ticket") return isNorwegian(lang) ? "Billett" : "TICKET";

  if (type === "travel_ticket")
    return isNorwegian(lang) ? "Reisebillett" : "TRAVEL TICKET";

  if (type === "credit") return isNorwegian(lang) ? "Gavekort" : "Gift card";

  return type;
};

export const getControlNumberLabel = (type: string): string => {
  switch (type.toLowerCase()) {
    case "no_kid":
      return "KID";
    case "dk_kid":
      return "FIK";
    case "se_ocr":
      return "OCR";
    default:
      return type;
  }
};
